/**
 * Pure helper functions for calendar business logic (no I/O)
 */

import type { PynRoomType, PynPlan, PynReservation } from '@/types/pyn-api';
import type { CalendarDay } from '@/types/calendar';

/**
 * Minimal festive-plan shape needed to flag festive days. The target window is
 * OPTIONAL (nullable) — a festive plan is identified by its `is_festive` flag,
 * not by having dates, so the highlight works on PYN data alone when no window
 * is set. When a window IS set it acts as an extra signal.
 */
export interface FestivePlanWindow {
  pynPlanId: string;
  targetCheckin: string | null; // YYYY-MM-DD or null
  targetCheckout: string | null; // YYYY-MM-DD or null
}

/**
 * Format date as YYYY-MM-DD in local timezone (not UTC)
 * Using toISOString() causes timezone shift issues
 */
function formatDateLocal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get all valid rate plans for a room type
 * - Filters plans where HavePrice === 1 AND StandardPrices?.Days exists
 * - Sorted by MediumPrice ascending (cheapest first)
 */
export function getAllValidPlans(roomTypeData: PynRoomType): PynPlan[] {
  const plans = Object.values(roomTypeData.Plans || {});

  const validPlans = plans.filter(
    (plan) =>
      plan.HavePrice === 1 &&
      plan.StandardPrices?.Days &&
      Object.keys(plan.StandardPrices.Days).length > 0
  );

  validPlans.sort((a, b) => {
    const priceA = a.StandardPrices?.MediumPrice ?? Infinity;
    const priceB = b.StandardPrices?.MediumPrice ?? Infinity;
    return priceA - priceB;
  });

  return validPlans;
}

/**
 * Get cheapest rate plan for a room type
 * - Filters plans where HavePrice === 1 AND StandardPrices?.Days exists
 * - Sorts by StandardPrices.MediumPrice ascending
 * - Returns first (cheapest) or null
 */
export function getCheapestValidPlan(roomTypeData: PynRoomType): PynPlan | null {
  const plans = getAllValidPlans(roomTypeData);
  return plans[0] || null;
}

/**
 * Build set of blocked dates from reservations (excluding checkout dates)
 * - Filters reservations by roomId === pmsRoomNumber
 * - Adds dates from checkIn to day BEFORE checkOut (checkout day is available)
 */
export function buildBlockedDatesSet(
  reservations: PynReservation[],
  pmsRoomNumber: string
): Set<string> {
  const blockedDates = new Set<string>();

  const roomReservations = reservations.filter(
    (res) => res.roomId === pmsRoomNumber
  );

  roomReservations.forEach((reservation) => {
    const checkIn = new Date(reservation.checkInDate);
    const checkOut = new Date(reservation.checkOutDate);

    // Block from check-in to day before checkout (checkout day is available)
    for (
      let d = new Date(checkIn);
      d < checkOut;
      d.setDate(d.getDate() + 1)
    ) {
      const dateStr = formatDateLocal(d);
      blockedDates.add(dateStr);
    }
  });

  return blockedDates;
}

/**
 * Compute the (year, month) immediately after the given one. Months are
 * 1-indexed. Pure — lives here (not in calendar-service) so the cache warmer
 * can walk the forward-month window without a static import cycle (doc 85).
 */
export function nextYearMonth(year: number, month: number): { year: number; month: number } {
  return month === 12 ? { year: year + 1, month: 1 } : { year, month: month + 1 };
}

/**
 * Get query date range for a given month
 * - Start a few days before month start (for spillover reservations)
 * - End a few days after month end
 */
export function getQueryDateRange(
  year: number,
  month: number
): { startDate: string; endDate: string; checkin: string; checkout: string; days: number } {
  // Start 5 days before the month (for reservations spanning into the month)
  let startDate = new Date(year, month - 1, -4); // month is 1-indexed

  // PYN reservation API rejects past dates — clamp start to today
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (startDate < today) {
    startDate = today;
  }

  const startDateStr = formatDateLocal(startDate);

  // End 5 days after the month
  const endDate = new Date(year, month, 5); // Next month + 5 days
  const endDateStr = formatDateLocal(endDate);

  // Calculate days difference (minimum 1 to avoid invalid API calls for past months)
  const days = Math.max(1, Math.ceil(
    (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
  ));

  // For Hotel Info API: checkin is first day of month, checkout is first day of next month
  const checkin = formatDateLocal(new Date(year, month - 1, 1));
  const checkout = formatDateLocal(new Date(year, month, 1));

  return {
    startDate: startDateStr,
    endDate: endDateStr,
    checkin,
    checkout,
    days: Math.min(days, 31), // Max 31 for API
  };
}

/**
 * Per-day plan affordance (doc 81 §3). Returns this plan's price on `date`
 * when the plan is open AND priced there, otherwise null. A plan is "open
 * with price" on a date when:
 *   - it advertises a price (HavePrice === 1 — defensive; callers usually
 *     pass `getAllValidPlans` output which already filters this),
 *   - it is not flagged Closed on that date,
 *   - and `StandardPrices.Days[date]` is a finite number > 0.
 *
 * This is the single source of truth for "is plan P bookable on day D",
 * shared by the calendar per-day winner and the checkout range filter so the
 * two can never drift.
 */
export function planDayPrice(plan: PynPlan, date: string): number | null {
  if (plan.HavePrice !== 1) return null;
  if ((plan.Closed || {})[date] === '1') return null;
  const price = plan.StandardPrices?.Days?.[date];
  if (typeof price !== 'number' || !Number.isFinite(price) || price <= 0) {
    return null;
  }
  return price;
}

/**
 * No-leapfrog range check (D23). True iff THIS single plan is open with a
 * price on every night in the range — i.e. the plan alone can carry the
 * whole stay. `stayNights` must be check-in inclusive, check-out EXCLUSIVE
 * (this is exactly what `getDatesBetween(checkIn, checkOut)` returns, so do
 * not slice it again at call sites).
 */
export function planCoversRange(plan: PynPlan, stayNights: string[]): boolean {
  if (stayNights.length === 0) return false;
  return stayNights.every((date) => planDayPrice(plan, date) !== null);
}

/**
 * Minimum-stay nights this plan requires for an arrival on `date` (doc 81
 * §11.3/§12). Reads `plan.MinStays[date]` — keyed by check-in date — and
 * floors at 1. Evergreen plans omit `MinStays` entirely ⇒ 1. A malformed /
 * non-numeric entry also degrades to 1 (never blocks a stay on bad data).
 */
export function planMinStay(plan: PynPlan, date: string): number {
  const raw = plan.MinStays?.[date];
  const n = raw === undefined ? 1 : Number(raw);
  return Number.isFinite(n) && n >= 1 ? n : 1;
}

/**
 * Per-plan min-stay gate (doc 81 §12.4 step C). True iff a stay CHECKING IN on
 * `checkIn` for `nights` nights satisfies THIS plan's minimum. `nights` is the
 * night count (check-out exclusive). Authoritative server-side check at
 * checkout, paired with `planCoversRange`.
 */
export function planMeetsMinStay(plan: PynPlan, checkIn: string, nights: number): boolean {
  return nights >= planMinStay(plan, checkIn);
}

/**
 * No-leapfrog range check (D23, doc 81 §4.4) extended with festive min-stay
 * (D30, §12.4 D). A range is bookable iff ≥1 single plan is open on EVERY
 * night AND the stay length meets the arrival day's aggregate min-stay.
 * `nights` must be check-out EXCLUSIVE (`getDatesBetween` output) — do NOT
 * slice it again. A missing/hard-unavailable night ⇒ not bookable.
 */
export function rangeBookable(dayMap: Map<string, CalendarDay>, nights: string[]): boolean {
  if (nights.length === 0) return false;
  const perNightSets: Set<string>[] = [];
  for (const date of nights) {
    const day = dayMap.get(date);
    if (!day || !day.available) return false;
    perNightSets.push(new Set(day.openPlanIds));
  }
  // Festive min-stay (D30): the stay must reach the arrival day's minimum.
  const arrivalMinStay = dayMap.get(nights[0])?.minStay ?? 1;
  if (nights.length < arrivalMinStay) return false;
  // Intersect open-plan sets across all nights: ≥1 survivor ⇒ a single plan
  // spans the whole range.
  const [first, ...rest] = perNightSets;
  return [...first].some((id) => rest.every((s) => s.has(id)));
}

/**
 * Best-rate ("green / cheaper") computation for a rendered month.
 *
 * Considers only bookable, non-past, priced days — AND deliberately EXCLUDES
 * festive days (`isFestive`). A festive package (e.g. plan 14's Jan 12–14) is
 * usually the priciest thing in the month and is often the only open plan on
 * its core dates, so leaving it in would inflate `max` and flip
 * `hasPriceVariation` true even when every ordinary day is the same price —
 * making the whole rest of the month light up green as "cheaper" purely
 * because a holiday is expensive. Festive days render gold (not green) anyway,
 * so dropping them here costs nothing and measures variation among the
 * ordinary days only. `hasPriceVariation` is false (⇒ no green at all) when
 * fewer than two distinct non-festive prices exist.
 */
export function computeBestRate(days: CalendarDay[]): {
  bestRatePrice: number | null;
  hasPriceVariation: boolean;
} {
  const prices = days
    .filter((d) => d.available && !d.isPast && d.price !== null && !d.isFestive)
    .map((d) => d.price as number);
  if (prices.length === 0) return { bestRatePrice: null, hasPriceVariation: false };
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return { bestRatePrice: min, hasPriceVariation: min !== max };
}

/**
 * Compute the minimum daily price advertised across ALL given rate plans for
 * non-past, non-closed, priced dates. Replaces the old per-plan
 * `computeMinPriceFromPlan`: the "starting from" figure is now the cheapest
 * open price any plan offers on any future date, matching the per-day winner
 * model (doc 81 §3). Independent of per-day availability/reservations — the
 * rate-card price stays correct even when the window is fully booked.
 */
export function computeMinPriceAcrossPlans(plans: PynPlan[]): number | null {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = formatDateLocal(today);

  const prices: number[] = [];
  for (const plan of plans) {
    const days = plan.StandardPrices?.Days || {};
    for (const date of Object.keys(days)) {
      if (date < todayStr) continue;
      const price = planDayPrice(plan, date);
      if (price !== null) prices.push(price);
    }
  }
  return prices.length > 0 ? Math.min(...prices) : null;
}

/**
 * Generate calendar days using the per-day winner model (doc 81 §3).
 *
 * For each day we evaluate every valid plan: the day's price is the cheapest
 * open plan's price, and `openPlanIds` lists all plans open that day. The day
 * is bookable when it isn't reserved, the room type has availability, AND at
 * least one plan is open (candidates non-empty). The calendar UI stays
 * plan-agnostic (D24) — no single "selected plan" drives the month.
 *
 * `festivePlans` flags festive days (union hybrid): a day is festive when one
 * of those plans' PYN ids is open+priced that day (PYN = source of truth) OR
 * the day falls inside that plan's CMS `targetCheckin..targetCheckout` window
 * when one is set (inclusive; an optional nice-to-have that survives PYN cache
 * gaps). The window is OPTIONAL — a festive plan with no dates still lights up
 * via PYN alone. Empty list ⇒ no day is ever flagged.
 */
export function generateCalendarDays(params: {
  year: number;
  month: number;
  blockedDates: Set<string>;
  availabilities: Record<string, string>;
  plans: PynPlan[];
  festivePlans?: FestivePlanWindow[];
}): CalendarDay[] {
  const { year, month, blockedDates, availabilities, plans, festivePlans = [] } = params;

  const calendar: CalendarDay[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = formatDateLocal(today);

  // Get first and last day of month
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0); // Last day of the month

  for (
    let d = new Date(firstDay);
    d <= lastDay;
    d.setDate(d.getDate() + 1)
  ) {
    const dateStr = formatDateLocal(d);

    // Per-day winner: collect every plan open+priced on this date. The
    // aggregate min-stay is the smallest min any OPEN plan requires for an
    // arrival here (floored at 1) — a day with any 1-night plan open stays
    // 1-night-bookable; a festive-only day inherits the festive minimum.
    const openPlanIds: string[] = [];
    let bestPrice: number | null = null;
    let minStay = 1;
    for (const plan of plans) {
      const price = planDayPrice(plan, dateStr);
      if (price === null) continue;
      const wasEmpty = openPlanIds.length === 0;
      const planMin = planMinStay(plan, dateStr);
      minStay = wasEmpty ? planMin : Math.min(minStay, planMin);
      openPlanIds.push(plan.ID);
      if (bestPrice === null || price < bestPrice) bestPrice = price;
    }

    // Check 1: Is this room reserved?
    const hasReservation = blockedDates.has(dateStr);

    // Check 2: Is this room type available in booking engine?
    // Missing dates = zero availability
    const availCount = availabilities[dateStr];
    const roomTypeAvailable =
      availCount !== undefined && parseInt(availCount, 10) > 0;

    // Available if: no reservation AND room type available AND ≥1 plan open.
    const isAvailable =
      !hasReservation && roomTypeAvailable && openPlanIds.length > 0;

    const isPast = d < today;
    const isToday = dateStr === todayStr;

    // Festive flag (union hybrid): PYN open for a festive plan (source of
    // truth) OR — only when a window is configured — inside its declared CMS
    // window (inclusive of both endpoints, a visual band).
    const isFestive = festivePlans.some(
      (fp) =>
        openPlanIds.includes(fp.pynPlanId) ||
        (fp.targetCheckin !== null &&
          fp.targetCheckout !== null &&
          dateStr >= fp.targetCheckin &&
          dateStr <= fp.targetCheckout)
    );

    calendar.push({
      date: dateStr,
      available: isAvailable,
      // Cheapest open plan's rate-card price for the day, independent of
      // availability (price shows even on greyed/booked days).
      price: bestPrice,
      isPast,
      isToday,
      openPlanIds,
      isFestive,
      // No open plan ⇒ minStay stays 1 (irrelevant; day is unavailable).
      minStay,
    });
  }

  return calendar;
}
